import React, { useState, useEffect, useContext, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { fetchOrders, getRestaurantProfile } from "../../utils/api";
import AuthContext from "../../contexts/AuthContext";
import {
  FaFileCsv,
  FaChartBar,
  FaListAlt,
  FaFileAlt,
  FaChevronDown,
  FaChevronUp,
  FaCalendarAlt,
  FaFilePdf,
  FaShare,
  FaDownload,
  FaTimes,
  FaPrint,
  FaEye,
  FaEyeSlash
} from "react-icons/fa";

// Report Types
const reportTypes = [
  { id: "summary", name: "Sales Summary", icon: <FaChartBar /> },
  { id: "itemSales", name: "Item-wise Sales", icon: <FaListAlt /> },
  { id: "fullHistory", name: "Detailed Order History", icon: <FaFileAlt /> },
];

// Number of items to show per page
const ITEMS_PER_PAGE = 10;

export default function ReportsScreen() {
  const { restaurantId } = useContext(AuthContext);
  const [restaurant, setRestaurant] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState([
    new Date(new Date().setDate(new Date().getDate() - 7)),
    new Date(),
  ]);
  const [startDate, endDate] = dateRange;
  const [activeReport, setActiveReport] = useState("summary");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (restaurantId) {
      setLoading(true);
      Promise.all([
        fetchOrders(restaurantId, startDate.toISOString(), endDate.toISOString()),
        getRestaurantProfile(restaurantId),
      ])
        .then(([orderData, profileData]) => {
          setOrders(orderData || []);
          setRestaurant(profileData);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching report data:", err);
          setLoading(false);
        });
    }
  }, [restaurantId, dateRange, activeReport]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleExport = () => {
    // Convert orders to CSV
    const csvContent = convertToCSV(orders, activeReport);
    
    // Create download link
    const encodedUri = encodeURI("data:text/csv;charset=utf-8," + csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    
    // Set filename based on report type and date
    const fileName = `${activeReport}_report_${formatDateForFilename(startDate)}_to_${formatDateForFilename(endDate)}.csv`;
    link.setAttribute("download", fileName);
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const convertToCSV = (orders, reportType) => {
    let csvContent = "";
    
    switch(reportType) {
      case "summary":
        const summaryData = getSummaryData(orders);
        csvContent = "Metric,Value\n";
        summaryData.forEach(item => {
          csvContent += `"${item.label}","${item.value}"\n`;
        });
        break;
        
      case "itemSales":
        const itemData = getItemSalesData(orders);
        csvContent = "Item,Quantity,Total Revenue\n";
        itemData.forEach(item => {
          csvContent += `"${item.name}","${item.qty}","${item.total.toFixed(2)}"\n`;
        });
        break;
        
      case "fullHistory":
        csvContent = "Order ID,Date,Table,Captain,Amount\n";
        orders.forEach(order => {
          const orderDate = new Date(order.createdAt._seconds * 1000).toLocaleDateString();
          const amount = (order.total * 1.05).toFixed(2);
          csvContent += `"${order.id.slice(-5)}","${orderDate}","${order.tableName || `Table ${order.tableId}`}","${order.captainName || "Unassigned"}","${amount}"\n`;
        });
        break;
        
      default:
        csvContent = "No data available";
    }
    
    return csvContent;
  };

  const formatDateForFilename = (date) => {
    return date.toISOString().split('T')[0]; // YYYY-MM-DD format
  };

  const handleGeneratePDF = () => {
    setPdfPreviewOpen(true);
  };

  const closePdfPreview = () => {
    setPdfPreviewOpen(false);
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 bg-white shadow-sm flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Reports & Analytics</h1>
        <button
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-lg text-sm shadow-md hover:shadow-lg transition-all"
          onClick={() => setFiltersOpen(!filtersOpen)}
        >
          {filtersOpen ? "Hide Filters" : "Show Filters"}
          {filtersOpen ? <FaChevronUp /> : <FaChevronDown />}
        </button>
      </div>

      {/* Filters (collapsible accordion style) */}
      <AnimatePresence>
        {filtersOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="px-6 py-4 bg-white border-b border-gray-200 shadow-sm space-y-4"
          >
            {/* Report Type Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Report Type
              </label>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-300 rounded-lg shadow-sm hover:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
              >
                <div className="flex items-center gap-3">
                  {reportTypes.find(t => t.id === activeReport).icon}
                  <span className="text-gray-800">
                    {reportTypes.find(t => t.id === activeReport).name}
                  </span>
                </div>
                <FaChevronDown className={`text-gray-500 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              <AnimatePresence>
                {dropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
                  >
                    {reportTypes.map((type) => (
                      <button
                        key={type.id}
                        onClick={() => {
                          setActiveReport(type.id);
                          setDropdownOpen(false);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-purple-50 transition-colors ${
                          activeReport === type.id ? 'bg-purple-100 text-purple-700' : 'text-gray-700'
                        }`}
                      >
                        {type.icon}
                        {type.name}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Date Range */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Date Range
              </label>
              <div className="relative">
                <DatePicker
                  selectsRange
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update) => setDateRange(update)}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg shadow-sm hover:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                />
                <FaCalendarAlt className="absolute right-3 top-3.5 text-gray-400" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto px-6 py-6">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
          </div>
        ) : (
          <>
            <ReportHeader restaurant={restaurant} dateRange={dateRange} />
            {activeReport === "summary" && <SalesSummary orders={orders} />}
            {activeReport === "itemSales" && <ItemSalesReport orders={orders} />}
            {activeReport === "fullHistory" && (
              <DetailedOrderHistory orders={orders} />
            )}
          </>
        )}
      </div>

      {/* Bottom Action Bar */}
      <div className="px-6 py-4 bg-white border-t border-gray-200 shadow-md flex justify-between items-center">
        <button
          onClick={handleExport}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm shadow-sm hover:bg-gray-200 transition-colors"
        >
          <FaFileCsv /> Export CSV
        </button>
        
        <button
          onClick={handleGeneratePDF}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-700 text-white rounded-lg text-sm shadow-md hover:shadow-lg transition-all"
        >
          <FaFilePdf /> Generate PDF Report
        </button>
      </div>

      {/* PDF Preview Modal */}
      <AnimatePresence>
        {pdfPreviewOpen && (
          <PDFPreview 
            restaurant={restaurant} 
            dateRange={dateRange} 
            orders={orders} 
            activeReport={activeReport}
            reportTypes={reportTypes}
            onClose={closePdfPreview}
            onPrint={handlePrint}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

/* ===== PDF Preview Component ===== */
const PDFPreview = ({ restaurant, dateRange, orders, activeReport, reportTypes, onClose, onPrint }) => {
  const getReportContent = () => {
    switch(activeReport) {
      case "summary":
        const summaryData = getSummaryData(orders);
        return (
          <div className="mt-6">
            <h3 className="text-xl font-semibold mb-4 text-gray-800 border-b pb-2">Sales Summary</h3>
            <div className="grid grid-cols-2 gap-4">
              {summaryData.map((item, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">{item.label}</p>
                  <p className="text-lg font-bold text-gray-800 mt-1">{item.value}</p>
                </div>
              ))}
            </div>
          </div>
        );
        
      case "itemSales":
        const itemData = getItemSalesData(orders);
        return (
          <div className="mt-6">
            <h3 className="text-xl font-semibold mb-4 text-gray-800 border-b pb-2">Item-wise Sales</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left p-2">Item Name</th>
                  <th className="text-right p-2">Quantity</th>
                  <th className="text-right p-2">Total Revenue</th>
                </tr>
              </thead>
              <tbody>
                {itemData.map((item, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="p-2">{item.name}</td>
                    <td className="text-right p-2">{item.qty}</td>
                    <td className="text-right p-2 font-medium">₹{item.total.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        
      case "fullHistory":
        return (
          <div className="mt-6">
            <h3 className="text-xl font-semibold mb-4 text-gray-800 border-b pb-2">Detailed Order History</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left p-2">Order ID</th>
                  <th className="text-left p-2">Date</th>
                  <th className="text-left p-2">Table</th>
                  <th className="text-left p-2">Captain</th>
                  <th className="text-right p-2">Amount</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="p-2">#{order.id.slice(-5)}</td>
                    <td className="p-2">{new Date(order.createdAt._seconds * 1000).toLocaleDateString()}</td>
                    <td className="p-2">{order.tableName || `Table ${order.tableId}`}</td>
                    <td className="p-2">{order.captainName || "Unassigned"}</td>
                    <td className="text-right p-2 font-medium">₹{(order.total * 1.05).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        
      default:
        return <div>No data available</div>;
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <motion.div 
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-800">PDF Report Preview</h3>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FaTimes />
          </button>
        </div>
        
        {/* PDF Preview Content */}
        <div className="flex-1 overflow-auto p-6 bg-gray-100">
          <div className="bg-white p-8 rounded-lg shadow-inner border border-gray-200">
            {/* Report Header */}
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-800">{restaurant?.name}</h2>
              <p className="text-gray-600 mt-2">
                {dateRange[0]?.toLocaleDateString()} - {dateRange[1]?.toLocaleDateString()}
              </p>
              <p className="text-gray-500 text-sm mt-1">
                {reportTypes.find(t => t.id === activeReport).name}
              </p>
              <p className="text-gray-400 text-xs mt-4">
                Generated on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}
              </p>
            </div>
            
            {/* Report Content */}
            {getReportContent()}
            
            {/* Footer */}
            <div className="mt-12 pt-6 border-t border-gray-200 text-xs text-gray-500 text-center">
              <p>{restaurant?.name} - Sales Report</p>
              <p className="mt-1">Confidential - For internal use only</p>
            </div>
          </div>
        </div>
        
        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button 
            onClick={onPrint}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm shadow-sm hover:bg-gray-200 transition-colors"
          >
            <FaPrint /> Print
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm shadow-sm hover:bg-gray-200 transition-colors">
            <FaShare /> Share
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-700 text-white rounded-lg text-sm shadow-md hover:shadow-lg transition-all">
            <FaDownload /> Download PDF
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

/* ===== Report Header ===== */
const ReportHeader = ({ restaurant, dateRange }) => (
  <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl shadow-sm border border-purple-100">
    <h2 className="text-xl font-bold text-gray-800">{restaurant?.name}</h2>
    <p className="text-gray-600">
      <FaCalendarAlt className="inline mr-2 text-purple-500" />
      {dateRange[0]?.toLocaleDateString()} - {dateRange[1]?.toLocaleDateString()}
    </p>
  </div>
);

/* ===== Summary Report ===== */
const getSummaryData = (orders) => {
  const preTaxRevenue = orders.reduce((s, o) => s + o.total, 0);
  const totalRevenue = preTaxRevenue * 1.05;
  const totalOrders = orders.length;
  const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
  const totalTax = preTaxRevenue * 0.05;
  return [
    { label: "Total Revenue", value: `₹${totalRevenue.toFixed(2)}`, trend: "up" },
    { label: "Total Orders", value: totalOrders, trend: "up" },
    { label: "Avg. Order Value", value: `₹${avgOrderValue.toFixed(2)}`, trend: "neutral" },
    { label: "Total Tax (5%)", value: `₹${totalTax.toFixed(2)}`, trend: "up" },
  ];
};

const SalesSummary = ({ orders }) => {
  const stats = getSummaryData(orders);
  return (
    <div>
      <h3 className="text-lg font-semibold mb-4 text-gray-800">Sales Summary</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
          >
            <p className="text-sm text-gray-500 mb-2">{s.label}</p>
            <p className="text-2xl font-bold text-gray-800">{s.value}</p>
            <div className={`mt-3 inline-flex items-center px-2 py-1 rounded-full text-xs ${
              s.trend === "up" ? "bg-green-100 text-green-800" : 
              s.trend === "down" ? "bg-red-100 text-red-800" : 
              "bg-blue-100 text-blue-800"
            }`}>
              {s.trend === "up" ? "↑ Increase" : s.trend === "down" ? "↓ Decrease" : "→ Stable"}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

/* ===== Item Sales Report ===== */
const getItemSalesData = (orders) => {
  const itemSales = orders
    .flatMap((o) => o.items)
    .reduce((acc, item) => {
      if (!acc[item.name]) {
        acc[item.name] = { name: item.name, qty: 0, total: 0 };
      }
      acc[item.name].qty += item.qty;
      acc[item.name].total += item.price * item.qty * 1.05;
      return acc;
    }, {});
  return Object.values(itemSales).sort((a, b) => b.qty - a.qty);
};

const ItemSalesReport = ({ orders }) => {
  const sorted = getItemSalesData(orders);
  const [visibleItems, setVisibleItems] = useState(ITEMS_PER_PAGE);
  
  const showMoreItems = () => {
    setVisibleItems(prev => prev + ITEMS_PER_PAGE);
  };
  
  const showLessItems = () => {
    setVisibleItems(ITEMS_PER_PAGE);
  };
  
  return (
    <div>
      <h3 className="text-lg font-semibold mb-4 text-gray-800">Item-wise Sales</h3>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="grid grid-cols-3 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-700">
          <div>Item Name</div>
          <div>Quantity</div>
          <div className="text-right">Total Revenue</div>
        </div>
        <div className="divide-y divide-gray-100">
          {sorted.slice(0, visibleItems).map((item, idx) => (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: idx * 0.05 }}
              key={idx} 
              className="grid grid-cols-3 px-4 py-3 hover:bg-purple-50 transition-colors"
            >
              <div className="font-medium text-gray-800">{item.name}</div>
              <div className="text-gray-600">{item.qty}</div>
              <div className="text-right font-semibold text-purple-700">₹{item.total.toFixed(2)}</div>
            </motion.div>
          ))}
        </div>
        
        {/* View More / View Less Buttons */}
        {sorted.length > ITEMS_PER_PAGE && (
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex justify-center">
            {visibleItems < sorted.length ? (
              <button 
                onClick={showMoreItems}
                className="flex items-center gap-2 px-4 py-2 text-purple-600 hover:text-purple-800 transition-colors"
              >
                <FaEye /> View More ({sorted.length - visibleItems} more items)
              </button>
            ) : (
              <button 
                onClick={showLessItems}
                className="flex items-center gap-2 px-4 py-2 text-purple-600 hover:text-purple-800 transition-colors"
              >
                <FaEyeSlash /> View Less
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* ===== Detailed History ===== */
const DetailedOrderHistory = ({ orders }) => {
  const [visibleOrders, setVisibleOrders] = useState(ITEMS_PER_PAGE);
  
  const showMoreOrders = () => {
    setVisibleOrders(prev => prev + ITEMS_PER_PAGE);
  };
  
  const showLessOrders = () => {
    setVisibleOrders(ITEMS_PER_PAGE);
  };
  
  return (
    <div>
      <h3 className="text-lg font-semibold mb-4 text-gray-800">Order History</h3>
      <div className="space-y-3">
        {orders.slice(0, visibleOrders).map((o, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.03 }}
            className="p-4 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow flex justify-between items-center"
          >
            <div>
              <p className="text-sm font-medium text-gray-800">#{o.id.slice(-5)}</p>
              <p className="text-xs text-gray-500 mt-1">
                {new Date(o.createdAt._seconds * 1000).toLocaleDateString()}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {o.tableName || `Table ${o.tableId}`} - {o.captainName || "Unassigned"}
              </p>
            </div>
            <p className="text-lg font-bold text-purple-700">₹{(o.total * 1.05).toFixed(2)}</p>
          </motion.div>
        ))}
      </div>
      
      {/* View More / View Less Buttons */}
      {orders.length > ITEMS_PER_PAGE && (
        <div className="mt-4 flex justify-center">
          {visibleOrders < orders.length ? (
            <button 
              onClick={showMoreOrders}
              className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
            >
              <FaEye /> View More ({orders.length - visibleOrders} more orders)
            </button>
          ) : (
            <button 
              onClick={showLessOrders}
              className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
            >
              <FaEyeSlash /> View Less
            </button>
          )}
        </div>
      )}
    </div>
  );
};